import * as React from 'react'
import { Loader2, Play, TriangleAlert, Video } from 'lucide-react'
import type { TreeVideoDto } from '@lib/dto/tree-video.dto'
import type { VideoOwner } from '@lib/input/tree-video.input'
import { MAX_VIDEO_SECONDS } from '@lib/constants/documents'
import {
  useArchiveVideo,
  useUploadVideo,
  useVideo,
  useVideos,
} from '@/hooks/useVideos'
import { errorMessage } from '@/helpers/logger'
import { uploadingLabel } from '@/helpers/uploadLabel'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Why Add is disabled, in the same two cases as photos.
 *
 * Offline, the survey can still be filled in and will sync, but a video has
 * to wait for a connection. While the survey itself is still saving, there is
 * nothing to attach the video to yet.
 */
function renderUnavailableReason(
  isOnline: boolean,
  isPendingSync: boolean,
): React.ReactNode {
  if (!isOnline) {
    return (
      <p className="text-xs text-muted-text">
        Videos need a connection. What you record now syncs on its own, but a
        video has to wait until you are back online.
      </p>
    )
  }
  if (isPendingSync) {
    return (
      <p className="text-xs text-muted-text">
        Saving this first — videos can be added in a moment.
      </p>
    )
  }
  return null
}

/** `95` becomes `1:35`, which is how anybody reads a clip length. */
function formatDuration(seconds: number | null): string | null {
  if (seconds === null) return null
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return `${String(minutes)}:${String(rest).padStart(2, '0')}`
}

/**
 * One video tile.
 *
 * Three states, and they look different on purpose: a still with a play badge
 * once Stream has finished, a spinner while it is transcoding, and a plain
 * warning when it refused the file. A single tile that merely failed to load
 * its thumbnail would leave somebody re-tapping a video that is never coming.
 */
function VideoTile({
  video,
  onOpen,
  onRemove,
  isRemoving,
}: {
  video: TreeVideoDto
  onOpen: () => void
  onRemove: () => void
  isRemoving: boolean
}) {
  const duration = formatDuration(video.durationSeconds)

  return (
    <li className="relative aspect-square overflow-hidden rounded-md border border-default bg-subtle">
      {video.status === 'READY' && video.thumbnailUrl !== null ? (
        <button
          type="button"
          onClick={onOpen}
          className="block h-full w-full"
          aria-label={`Play ${video.fileName ?? 'video'}`}
        >
          <img
            src={video.thumbnailUrl}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-map-ink/70">
              <Play className="h-4 w-4 fill-map-halo text-map-halo" />
            </span>
          </span>
          {duration === null ? null : (
            <span className="absolute bottom-1 left-1 rounded-[3px] bg-map-ink/70 px-1 text-[10px] font-medium text-map-halo tabular-nums">
              {duration}
            </span>
          )}
        </button>
      ) : null}

      {video.status === 'PENDING_UPLOAD' || video.status === 'PROCESSING' ? (
        <div className="flex h-full flex-col items-center justify-center gap-1 px-1 text-center">
          <Loader2 className="h-4 w-4 animate-spin text-muted-text" />
          <span className="text-[10px] leading-tight text-muted-text">
            Processing
          </span>
        </div>
      ) : null}

      {video.status === 'ERRORED' ? (
        <div className="flex h-full flex-col items-center justify-center gap-1 px-1 text-center">
          <TriangleAlert className="h-4 w-4 text-danger" />
          <span className="text-[10px] leading-tight text-danger">
            {video.errorMessage ?? 'Failed'}
          </span>
        </div>
      ) : null}

      <button
        type="button"
        onClick={onRemove}
        disabled={isRemoving}
        aria-label={`Remove ${video.fileName ?? 'video'}`}
        // Always visible, like the photo tiles': this lives on a phone in a
        // park, where there is no hover to reveal anything with.
        className="absolute top-1 right-1 flex h-7 w-7 items-center justify-center rounded-md bg-map-ink/70 text-map-halo transition-opacity hover:bg-map-ink disabled:opacity-50"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  )
}

/**
 * Videos of one survey subject, a tree or an irrigation record, on Cloudflare
 * Stream. Shared by both survey screens.
 *
 * Stream rather than the bucket the photos live in, because video is not a file
 * problem: it needs transcoding, a poster frame, and a rendition a rural
 * connection can actually carry. The trade is that an upload is not finished
 * when the bytes land — Stream re-encodes afterwards — so a tile says
 * "Processing" and the list polls until it settles.
 *
 * Always renders. Callers hide it when Stream is not configured
 * (`useVideoAvailability` on the tree and irrigation survey screens), so an
 * unconfigured deployment looks absent there rather than broken here.
 */
export function SurveyVideos({
  owner,
  visitId,
  isOnline,
  isPendingSync,
  controlClassName,
}: {
  owner: VideoOwner
  /**
   * For a tree, the visit the survey is working in: its videos, and where a
   * new one goes. Null lists every visit's. Not read for anything else.
   */
  visitId?: string | null
  isOnline: boolean
  isPendingSync: boolean
  controlClassName?: string
}) {
  const treeVisit = 'treeId' in owner ? visitId : undefined
  const videos = useVideos(owner, {
    enabled: !isPendingSync,
    visitId: treeVisit ?? null,
  })
  const [percent, setPercent] = React.useState<number | null>(null)
  const upload = useUploadVideo(owner, setPercent, treeVisit)
  const archive = useArchiveVideo(owner)
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [playing, setPlaying] = React.useState<TreeVideoDto | null>(null)
  // A fresh link when the dialog opens. The list stops refreshing once a clip
  // is ready, and the playback token on that row expires after an hour.
  const playback = useVideo(playing?.id ?? null)

  const canAttach = isOnline && !isPendingSync

  function renderPlayback(): React.ReactNode {
    if (playing === null) return null
    if (playback.isError) {
      return (
        <p className="p-4 text-sm text-danger">
          {errorMessage(playback.error, 'The video could not be played.')}
        </p>
      )
    }
    if (playback.data === undefined) {
      return (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-text" />
        </div>
      )
    }
    const url = playback.data.playbackUrl
    if (url === null) {
      return (
        <p className="p-4 text-sm text-muted-text">
          This video cannot be played yet.
        </p>
      )
    }
    return (
      /*
        Stream's own iframe player rather than a <video> tag. It handles
        HLS, adaptive bitrate and the signed token in the URL; a bare
        video element would need an HLS library and would still not play
        on the browsers that lack native support.
      */
      <iframe
        src={url}
        title={playing.fileName ?? 'Video'}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen
        className="aspect-video w-full rounded-lg border-0"
      />
    )
  }
  const items = videos.data ?? []
  // Same rule as photos: a failed list is not an empty one, and Add waits
  // until the videos already on this subject are known.

  async function handleFile(files: FileList | null): Promise<void> {
    if (files === null || files.length === 0) return
    setError(null)
    const [file] = Array.from(files)
    if (file === undefined) return
    setPercent(null)
    try {
      await upload.mutateAsync(file)
    } catch (caught) {
      setError(errorMessage(caught, 'The video could not be saved.'))
    }
    if (inputRef.current !== null) {
      inputRef.current.value = ''
    }
  }

  async function handleRemove(id: string): Promise<void> {
    setError(null)
    try {
      await archive.mutateAsync(id)
    } catch (caught) {
      setError(errorMessage(caught, 'The video could not be saved.'))
    }
  }

  function renderVideoList(): React.ReactNode {
    if (videos.isLoading) {
      return (
        <div className="flex justify-center py-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-text" />
        </div>
      )
    }
    if (videos.isError) {
      return <p className="text-xs text-danger">{errorMessage(videos.error, 'The video could not be saved.')}</p>
    }
    if (items.length === 0) return null
    return (
      <ul className="grid grid-cols-3 gap-1.5">
        {items.map((video) => (
          <VideoTile
            key={video.id}
            video={video}
            isRemoving={archive.isPending}
            onOpen={() => {
              setPlaying(video)
            }}
            onRemove={() => {
              void handleRemove(video.id)
            }}
          />
        ))}
      </ul>
    )
  }

  return (
    <div className="space-y-1.5">
      <Label>Video</Label>

      {renderVideoList()}

      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        capture="environment"
        className="hidden"
        onChange={(event) => {
          void handleFile(event.target.files)
        }}
      />

      <Button
        variant="outline"
        className={cn('w-full', controlClassName)}
        disabled={!canAttach || upload.isPending || !videos.isSuccess}
        onClick={() => {
          inputRef.current?.click()
        }}
      >
        {upload.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Video className="h-4 w-4" />
        )}
        {upload.isPending ? uploadingLabel(percent) : 'Add video'}
      </Button>

      {renderUnavailableReason(isOnline, isPendingSync)}

      <p className="text-xs text-muted-text">
        Up to {Math.floor(MAX_VIDEO_SECONDS / 60)} minutes. It takes a moment to
        process after uploading.
      </p>
      {error === null ? null : <p className="text-xs text-danger">{error}</p>}

      <Dialog
        open={playing !== null}
        onOpenChange={(open) => {
          if (!open) setPlaying(null)
        }}
      >
        <DialogContent className="max-w-2xl p-0">
          <DialogTitle className="sr-only">
            {playing?.fileName ?? 'Video'}
          </DialogTitle>
          {renderPlayback()}
        </DialogContent>
      </Dialog>
    </div>
  )
}
